import { useState } from "react";
import { Button, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import { SerialEntity } from "models/Checklist";
import SaveIcon from "@mui/icons-material/Save";
import { useEditSerialEntityMutation } from "app/api/checklists";
import { useOrderContext } from "context/OrderContext";
import LargeDialog from "components/LargeDialog";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import AlertAccordion from "components/AlertAccordion";
import { UserRole } from "features/auth/types/user";
import { useAuthContext } from "features/auth/AuthContext";

type Props = {
  serial: SerialEntity;
  onClose?: () => void;
};

const EditSerialEntityDialog: React.FC<Props> = ({ onClose, serial }) => {
  const order = useOrderContext();
  const { hasRole } = useAuthContext();
  const canEditSerial = hasRole([
    UserRole.ADMIN,
    UserRole.USER_ADMIN,
    UserRole.SALES,
    UserRole.INTERNAL_INSTALLER,
    UserRole.EXTERNAL_INSTALLER,
  ]);
  const [editProduct, { isLoading: isEditing }] = useEditSerialEntityMutation();

  const [showAllFields, setShowAllFields] = useState<boolean>(false);

  // Variables for new serial form
  const [serialNumber, setSerialNumber] = useState<string>(serial.serialNumber || "");
  const [maker, setMaker] = useState<string>(serial.maker || "");
  const [model, setModel] = useState<string>(serial.model || "");
  const [macAddressWired, setMacAddressWired] = useState<string>(serial.macAddress || "");
  const [ethernetPort, setEthernetPort] = useState<string>(serial.networkSocket || "");
  const [theftMarking, setTheftMarking] = useState<string>(serial.theftProtection || "");
  const [macAddressWifi, setMacAddressWifi] = useState<string>(serial.macAddressWifi || "");
  const [ipAddress, setIpAddress] = useState<string>(serial.ipAddress || "");
  const [username, setUsername] = useState<string>(serial.username || "");
  const [password, setPassword] = useState<string>(serial.password || "");
  const [other, setOther] = useState<string>(serial.other || "");

  const isNoFieldEmpty = Boolean(
    serialNumber &&
    maker &&
    model &&
    macAddressWired &&
    ethernetPort &&
    theftMarking &&
    macAddressWifi &&
    ipAddress &&
    username &&
    password &&
    other
  );

  const handleEditSerial = async () => {
    editProduct({
      orderId: order.ordernr,
      checklistId: serial.checklistId,
      id: serial.id,
      serialNumber: serialNumber,
      maker: maker,
      model: model,
      macAddress: macAddressWired,
      macAddressWifi: macAddressWifi,
      networkSocket: ethernetPort,
      theftProtection: theftMarking,
      ipAddress: ipAddress,
      username: username,
      password: password,
      other: other,
    })
      .unwrap()
      .then(() => {
        toast.success("Produkten har redigerats!");
        onClose?.();
      })
      .catch(() => {
        toast.error("Något gick fel när produkten skulle redigeras.");
      });
  };

  return (
    <LargeDialog
      onClose={() => onClose?.()}
      title="Redigera produkt"
      onSubmit={(e) => {
        e.preventDefault();
        handleEditSerial();
      }}
      actions={
        <>
          <Button
            variant="outlined"
            disabled={isEditing}
            startIcon={<CloseIcon />}
            onClick={onClose}
          >
            Avbryt
          </Button>

          <Button
            type="submit"
            variant="contained"
            color="success"
            startIcon={<SaveIcon />}
            loading={isEditing}
          >
            Spara
          </Button>
        </>
      }
    >
      <AlertAccordion severity="info" title="Befintlig/manuellt registrerad produkt">
        <Typography gutterBottom>
          Denna produkt är inte kopplad till någon artikelrad i Pyramid.
        </Typography>

        <Typography>
          Antingen är det en befintlig produkt som har registrerats här i dokumentationssyfte, eller
          så har den sparats i en äldre version av appen.
        </Typography>
      </AlertAccordion>

      <TextField
        variant="filled"
        label="Serienummer"
        disabled={!canEditSerial}
        fullWidth
        required
        value={serialNumber}
        onChange={(e) => setSerialNumber(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Tillverkare"
        fullWidth
        required
        value={maker}
        onChange={(e) => setMaker(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Modell"
        fullWidth
        required
        value={model}
        onChange={(e) => setModel(e.target.value)}
      />
      <TextField
        variant="filled"
        label="MAC-adress"
        hidden={!showAllFields && !macAddressWired}
        fullWidth
        value={macAddressWired}
        onChange={(e) => setMacAddressWired(e.target.value)}
      />
      <TextField
        variant="filled"
        label="MAC-adress Wi-Fi"
        hidden={!showAllFields && !macAddressWifi}
        fullWidth
        value={macAddressWifi}
        onChange={(e) => setMacAddressWifi(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Nätverksuttag"
        hidden={!showAllFields && !ethernetPort}
        fullWidth
        value={ethernetPort}
        onChange={(e) => setEthernetPort(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Stöldmärkning"
        hidden={!showAllFields && !theftMarking}
        fullWidth
        value={theftMarking}
        onChange={(e) => setTheftMarking(e.target.value)}
      />
      <TextField
        variant="filled"
        label="IP-adress (om fast)"
        hidden={!showAllFields && !ipAddress}
        fullWidth
        value={ipAddress}
        onChange={(e) => setIpAddress(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Användarnamn"
        hidden={!showAllFields && !username}
        fullWidth
        value={username}
        onChange={(e) => setUsername(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Lösenord"
        hidden={!showAllFields && !password}
        fullWidth
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Övrigt"
        hidden={!showAllFields && !other}
        fullWidth
        value={other}
        onChange={(e) => setOther(e.target.value)}
      />

      <Button
        hidden={isNoFieldEmpty}
        startIcon={showAllFields ? <ExpandLess /> : <ExpandMore />}
        onClick={() => setShowAllFields(!showAllFields)}
        sx={{ mt: -1, mb: -2 }}
      >
        {showAllFields ? "Dölj tomma fält" : "Visa fler fält"}
      </Button>
    </LargeDialog>
  );
};

export default EditSerialEntityDialog;
